'use client';
import {Box} from '@mui/material';
import {styled} from '@mui/material/styles';
import {NavArrow} from '../../primitives';
import {carouselArrowSx} from '../carouselArrowSx';
import {useDirectionalScroller} from '../useDirectionalScroller';
import {FilterChip} from './FilterChip';

export interface ListingFilterItem {
  id: string;
  label: string;
}

export interface ListingFilterBarProps {
  items: ListingFilterItem[];
  activeId?: string;
  onSelect: (id?: string) => void;
  allLabel: string;
}

// Positioning context for the edge arrows, which flank the Bar from outside its
// bounds (in the Container gutter) via carouselArrowSx — matching the carousel.
const Root = styled(Box)(({theme}) => ({
  position: 'relative',
  // Mobile (Figma 2761-47549): the category row bleeds to both viewport edges.
  // Arrows are hidden below md, so the row escapes the Container's 18px gutter
  // with a negative inline margin; the Bar re-adds it as scroll padding (below)
  // so the first chip rests inset while the rest scroll under the edges.
  [theme.breakpoints.down('md')]: {
    marginInline: theme.spacing(-2.25),
  },
}));

const FADE_WIDTH = '24px';

const edgeStop = (faded: boolean) => (faded ? 'transparent' : 'black');

const Bar = styled(Box, {shouldForwardProp: prop => prop !== 'fadeStart' && prop !== 'fadeEnd'})<{fadeStart: boolean; fadeEnd: boolean}>(({
  theme,
  fadeStart,
  fadeEnd,
}) => {
  const mask = `linear-gradient(to right, ${edgeStop(fadeStart)}, black ${FADE_WIDTH}, black calc(100% - ${FADE_WIDTH}), ${edgeStop(fadeEnd)})`;
  return {
    display: 'flex',
    gap: theme.spacing(1),
    overflowX: 'auto',
    scrollBehavior: 'smooth',
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': {display: 'none'},
    [theme.breakpoints.down('md')]: {
      paddingInline: theme.spacing(2.25),
      scrollPaddingInline: theme.spacing(2.25),
    },
    ...((fadeStart || fadeEnd) && {WebkitMaskImage: mask, maskImage: mask}),
  };
});

export function ListingFilterBar({items, activeId, onSelect, allLabel}: ListingFilterBarProps) {
  const {ref, canPrev, canNext, scrollByDir} = useDirectionalScroller<HTMLDivElement>(items.length);

  const showArrows = canPrev || canNext;

  return (
    <Root>
      {showArrows && (
        <NavArrow
          direction="prev"
          disabled={!canPrev}
          onClick={() => {
            scrollByDir(-1);
          }}
          sx={carouselArrowSx('prev')}
        />
      )}
      <Bar ref={ref} fadeStart={canPrev} fadeEnd={canNext}>
        <FilterChip
          label={allLabel}
          active={!activeId}
          onSelect={() => {
            onSelect(undefined);
          }}
        />
        {items.map(item => {
          const active = item.id === activeId;
          return (
            <FilterChip
              key={item.id}
              label={item.label}
              active={active}
              onSelect={() => {
                onSelect(item.id);
              }}
            />
          );
        })}
      </Bar>
      {showArrows && (
        <NavArrow
          direction="next"
          disabled={!canNext}
          onClick={() => {
            scrollByDir(1);
          }}
          sx={carouselArrowSx('next')}
        />
      )}
    </Root>
  );
}

ListingFilterBar.displayName = 'ListingFilterBar';
